import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getPendingClaimToken, stashClaimToken } from '@/lib/claimFlow';
import { stashSeekerToken } from '@/lib/seekerFlow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useScrollTopOnChange } from '@/lib/anchorScroll';

// Email OTP resend cooldown — mirrors Supabase's ~60 s server-side send
// window so users can never tap into a guaranteed rate-limit error (§3b G8).
const EMAIL_RESEND_COOLDOWN_SECONDS = 60;

type AuthStep = 'initial' | 'email_input' | 'otp_verify';

interface AuthPageProps {
  mode: 'login' | 'signup';
}

const AuthPage = ({ mode }: AuthPageProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<AuthStep>('initial');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  // A RESEND gets its own busy flag, deliberately NOT `loading`. `loading`
  // disables the code boxes, and disabling the focused input is what tears the
  // keyboard down on iOS — after which no amount of programmatic focus brings
  // it back, because iOS only opens the keyboard from a user gesture. The user
  // must be able to keep typing while the new code is in flight.
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Set when the flat.access_gate auth hook rejects token issuance (pre-public
  // beta: this email has no active access grant). We show a dedicated panel
  // rather than a raw error toast so the user understands they're not blocked
  // by a bug — access just isn't switched on for them yet.
  const [accessDenied, setAccessDenied] = useState(false);

  const isLogin = mode === 'login';

  // iOS opens the keyboard ONLY from a user gesture — an autoFocus that fires
  // after the OTP-send round-trip shows the caret but no keyboard. This hidden
  // numeric input is focused synchronously INSIDE the send tap; the code boxes
  // then take the focus over when they mount, so the numeric keyboard is
  // already up when the code screen appears (mockup: keyboard open from entry).
  const kbWarmupRef = useRef<HTMLInputElement>(null);
  // The real OTP input — resends refocus it so typing continues seamlessly
  // (the warm-up input must never keep focus once the boxes exist).
  const otpRef = useRef<HTMLInputElement>(null);
  /** Set by a resend; consumed once the code boxes are enabled again. */
  const refocusOtpRef = useRef(false);

  // Safety net: puts the caret back in the code boxes after a resend, on the
  // first render where they are actually focusable. With `resending` split out
  // of `loading` the boxes now stay enabled throughout, so focus should never
  // leave — but if anything else steals it, this restores it from the state
  // that gates the input rather than guessing how many frames a commit takes.
  useEffect(() => {
    if (!refocusOtpRef.current || loading || resending || step !== 'otp_verify') return;
    refocusOtpRef.current = false;
    otpRef.current?.focus();
  }, [loading, resending, step]);

  // Chooser→email→code swap in place — snap back to the top on each change.
  useScrollTopOnChange(step);

  // Claim-link visitors are owners — the renter value line must not greet them
  // (spec §3 N0). Checked from the URL and the stashed token (OAuth round-trips
  // land back here without the query param).
  const hasClaimToken = !!(searchParams.get('claim') ?? getPendingClaimToken());

  // Belt-and-braces for ?claim= / ?seeker= deeplinks: App-level *Capture
  // components normally stash these, but an OAuth round-trip that lands
  // straight on /signup must not depend on a sibling component's effect order.
  useEffect(() => {
    const claim = searchParams.get('claim');
    if (claim) stashClaimToken(claim);
    const seeker = searchParams.get('seeker');
    if (seeker) stashSeekerToken(seeker);
  }, [searchParams]);

  // Pre-fill the email from a temporary-access invite link (?access=<token>, or
  // the value AccessCapture stashed). Resolving the token → email server-side
  // means the invitee only has to enter the OTP.
  useEffect(() => {
    const token =
      searchParams.get('access') ??
      (typeof localStorage !== 'undefined' ? localStorage.getItem('fh_access_token') : null);
    if (!token) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.functions.invoke('access-lookup', { body: { token } });
      if (!cancelled && data?.found && typeof data.email === 'string') {
        setEmail(data.email);
        setStep('email_input');
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams]);

  // A Google-SSO denial from the auth hook comes back on the OAuth redirect as
  // an error_description in the URL hash, not through a promise — detect it here.
  useEffect(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (/access_not_active/.test(hash) || /access_not_active/.test(window.location.search)) {
      setAccessDenied(true);
    }
  }, []);

  // Cooldown ticker for the email-code resend row
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleGoogleSSO = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/hubabuba`,
        // Force the account chooser. Without this, Google silently re-auths
        // the browser's already-logged-in Google account, which means logging
        // out of Flat Hero and clicking "Sign up with Google" lands you
        // straight back in the previous user's profile.
        queryParams: { prompt: 'select_account' },
      },
    });
    if (error) {
      if (/access_not_active/.test(error.message)) setAccessDenied(true);
      else toast.error(error.message);
    }
  };

  const handleEmailOTPStart = () => {
    setStep('email_input');
  };

  const handleSendOTP = async () => {
    if (!email) return;
    // Warm the keyboard up ONLY for the initial send (email screen → code
    // screen). On a RESEND the code boxes already exist and keep/regain focus
    // below — warming up here would steal keystrokes into the hidden input.
    const isResend = step === 'otp_verify';
    if (!isResend) kbWarmupRef.current?.focus(); // synchronous, inside the tap
    const setBusy = isResend ? setResending : setLoading;
    setBusy(true);

    // An invite link (`?invite=<token>`) never shortens this step: the invitee confirms their
    // e-mail with the same OTP as everyone else. The old `verify-invite-email` shortcut minted a
    // magic link for any e-mail that matched the invite's `invite_email` — which the INVITER
    // chose — so whoever held the invite link could sign in as an address they did not own
    // (docs/security/AccessAudit.md F15, removed 30.10.2026). The invite token itself is only
    // read later, by BasicInfoStep / match-tenant-link, to attach the link.
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: !isLogin,
        emailRedirectTo: `${window.location.origin}/hubabuba`,
      },
    });
    setBusy(false);
    if (error) {
      // Failed send → drop the warmed-up keyboard; the user stays on this
      // screen and shouldn't be typing a code that never got sent.
      kbWarmupRef.current?.blur();
      // Supabase returns a generic error when shouldCreateUser=false and email not found;
      // surface a friendlier message for login mode.
      if (isLogin) {
        toast.error(t('auth.login_email_not_found'));
      } else {
        toast.error(error.message);
      }
    } else {
      setStep('otp_verify');
      setResendCooldown(EMAIL_RESEND_COOLDOWN_SECONDS);
      toast(t('auth.otp_sent', { email }));
      // After a resend the caret must stay in the code boxes so the next
      // keystrokes land there (A1: they used to fall into the warm-up input).
      // The effect above does it if focus was lost. It used to be a single
      // requestAnimationFrame — a bet that React commits the re-enabled input
      // within one frame, lost often enough to fail CI and lost on slow phones
      // too.
      if (isResend) refocusOtpRef.current = true;
    }
  };

  // Auto-submit when the code is complete — matches SmsVerifyStep so both
  // OTP screens behave the same way. The Confirm button below stays as a
  // fallback for desktop keyboard / paste flows.
  useEffect(() => {
    if (step === 'otp_verify' && otpCode.length === 6) {
      handleVerifyOTP();
    }
    // handleVerifyOTP is stable for the lifetime of this component (it only
    // reads the latest values via state), so it's safe to omit from the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpCode, step]);

  const handleVerifyOTP = async () => {
    if (otpCode.length !== 6 || loading) return;
    setLoading(true);
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token: otpCode,
      type: 'email',
    });
    if (error) {
      setLoading(false);
      setOtpCode('');
      if (/access_not_active/.test(error.message)) setAccessDenied(true);
      else toast.error(error.message);
    } else {
      // Existing users get a "welcome back" on login. New signups are NOT told
      // "account created" yet: verifying the OTP only proves the inbox — the
      // account isn't real until the user accepts the terms on the basic-info
      // step, so we stay quiet and let the redirect carry them forward.
      if (isLogin) toast.success(t('auth.login_success'), { duration: 5000 });
      // Keep loading=true so button stays disabled; App.tsx session listener handles redirect
    }
  };

  return (
    // MOBILE: top-anchored under the header (mockup) — critical for the
    // keyboard screens: with the input near the top, iOS never auto-scrolls
    // the page on focus, so the header + title + field + CTA all stay visible
    // above the keyboard. DESKTOP (md+): vertically centered in 100dvh minus
    // the site chrome above (header h-14 = 3.5rem; the announcement bar is
    // hidden on the auth funnel) — a plain min-h-screen box STARTS below the
    // header, so its center would land below the visual middle.
    // MOBILE: natural height — do NOT force 100dvh. A container taller than the
    // screen gives iOS slack to scroll when the keyboard opens, which slides the
    // back chip under the sticky header. With the content shorter than the
    // viewport there is nothing to scroll at all.
    // DESKTOP (md+): the forced height is what centers the card vertically
    // (100dvh minus the h-14 header; the announcement bar is hidden here).
    <div className="funnel-scale flex justify-center bg-background px-4 pt-6 pb-10 md:min-h-[calc(100dvh-3.5rem)] md:items-center md:pb-0 md:pt-0">
      <div className="relative w-full max-w-md space-y-8">
        {/* Always-mounted keyboard warm-up target (see kbWarmupRef). Kept
            outside the step conditionals so the focus hand-off to the code
            boxes never races an unmount.

            NOT sr-only: a 1px clipped input makes iOS zoom the page trying to
            bring the focused element into view (and the zoom persists across
            screens). It must be a normally-sized, in-viewport, 16px input —
            invisible via opacity, zero-height so it takes no layout space. */}
        <input
          ref={kbWarmupRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none absolute h-0 w-full border-0 bg-transparent p-0 text-base opacity-0"
        />
        {/* Title — method chooser only, and per the mockup NOT on the organic
            signup variant (the value line is the heading there); login and
            claim visitors (no value line) keep it. The email screens carry
            their own compact headers. */}
        {step === 'initial' && (isLogin || hasClaimToken) && (
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight text-foreground" style={{ fontFamily: 'var(--font-heading)' }}>
              {t(isLogin ? 'auth.login_title' : 'auth.signup_title')}
            </h1>
          </div>
        )}

        {accessDenied && (
          <div className="rounded-lg border border-amber-300 bg-warning-soft p-4 text-center space-y-2 dark:border-amber-800 dark:bg-amber-950">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              {t('auth.access_not_active_title')}
            </p>
            <p className="text-xs text-warning-soft-foreground dark:text-amber-300">
              {t('auth.access_not_active_body')}
            </p>
          </div>
        )}

        {!accessDenied && step === 'initial' && (
          <div className="space-y-4">
            {/* Value line — signup only. Now role-NEUTRAL: /signup is the shared
                front door for tenants and owners (the role is picked on the next
                screen), so tenant-only copy here spoke to half the visitors.
                Still suppressed for claim visitors, who arrive with their own
                context and don't need the generic pitch. */}
            {!isLogin && !hasClaimToken && (
              <p className="text-center text-sm text-muted-foreground leading-relaxed">
                {t('auth.value_prop')}
              </p>
            )}

            {/* Email OTP — the primary path (plain label, no icon — mockup) */}
            <Button
              className="w-full h-12 text-base"
              onClick={handleEmailOTPStart}
            >
              {t(isLogin ? 'auth.email_otp' : 'auth.email_signup_cta')}
            </Button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">{t('auth.or')}</span>
              </div>
            </div>

            {/* Google SSO */}
            <Button
              variant="outline"
              className="w-full h-12 text-base gap-3"
              onClick={handleGoogleSSO}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              {t('auth.sso_google')}
            </Button>

            {/* Facebook: provider not configured yet — rendered greyed-out as a
                deliberate reminder (Maciek 2026-07-25, overriding spec §8.3)
                until the FB app + Supabase provider land. Apple stays hidden. */}
            <Button variant="outline" className="w-full h-12 text-base gap-3" disabled>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              {t('auth.sso_facebook')}
            </Button>

            {/* Cross-link */}
            <p className="text-center text-sm text-muted-foreground">
              {isLogin ? (
                <>
                  {t('auth.no_account')}{' '}
                  <Link to="/signup" className="text-primary underline underline-offset-4">
                    {t('header.signup')}
                  </Link>
                </>
              ) : (
                <>
                  {t('auth.already_have_account')}{' '}
                  <Link to="/login" className="text-primary underline underline-offset-4">
                    {t('header.login')}
                  </Link>
                </>
              )}
            </p>
          </div>
        )}

        {!accessDenied && step === 'email_input' && (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full border border-border"
              onClick={() => setStep('initial')}
              aria-label={t('common.back')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2
              className="text-2xl font-bold tracking-tight text-foreground"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {t('auth.email_title')}
            </h2>
            <div className="space-y-2">
              <Label htmlFor="email" className="sr-only">Email</Label>
              {/* Single-field screen → auto-focus with the email keyboard open
                  (§3b G5); the keyboard's action key sends (enterkeyhint). */}
              <Input
                id="email"
                type="email"
                inputMode="email"
                enterKeyHint="send"
                autoComplete="email"
                placeholder="jan@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendOTP()}
                autoFocus
              />
            </div>
            <Button
              className="w-full h-12 text-base"
              onClick={handleSendOTP}
              // Keep the email keyboard from closing during the tap — the
              // warm-up focus then only has to SWITCH it to numeric.
              onPointerDown={(e) => e.preventDefault()}
              disabled={!email || loading}
            >
              {/* With an invite link the email usually matches and we skip the
                  OTP entirely — so "Send code" is misleading. Show "Next" instead. */}
              {loading ? t('common.loading') : t('auth.otp_send')}
            </Button>
          </div>
        )}

        {!accessDenied && step === 'otp_verify' && (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full border border-border"
              onClick={() => { setStep('email_input'); setOtpCode(''); }}
              aria-label={t('common.back')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="space-y-1">
              <h2
                className="text-2xl font-bold tracking-tight text-foreground"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {t('auth.email_code_title')}
              </h2>
              <p className="text-sm text-muted-foreground">{email}</p>
            </div>
            <InputOTP
              ref={otpRef}
              maxLength={6}
              value={otpCode}
              onChange={setOtpCode}
              disabled={loading}
              autoFocus
              autoComplete="one-time-code"
              inputMode="numeric"
              containerClassName="w-full"
            >
              {/* Separated, form-wide code boxes (mockup) — not the joined
                  stock strip; the aux row + CTA align underneath. */}
              <InputOTPGroup className="w-full gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <InputOTPSlot
                    key={i}
                    index={i}
                    className="h-14 flex-1 rounded-lg border text-lg first:rounded-lg last:rounded-lg"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
            {/* One-line aux row directly under the code boxes (§3b G3):
                resend with its 60 s countdown left, "Zmień e-mail" right —
                both disabled while a verification is in flight (G9). */}
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={handleSendOTP}
                onPointerDown={(e) => e.preventDefault()}
                disabled={resendCooldown > 0 || resending || loading}
                className="text-muted-foreground font-medium disabled:opacity-60"
              >
                {resendCooldown > 0
                  ? t('auth.resend_countdown', { seconds: resendCooldown })
                  : t('auth.otp_resend')}
              </button>
              <button
                type="button"
                onClick={() => { setStep('email_input'); setOtpCode(''); }}
                disabled={loading}
                className="text-primary font-semibold disabled:opacity-60"
              >
                {t('auth.change_email')}
              </button>
            </div>
            {/* Fallback CTA — auto-submit fires at the 6th digit (G9) */}
            <Button
              className="w-full h-12 text-base"
              onClick={handleVerifyOTP}
              disabled={otpCode.length !== 6 || loading}
            >
              {loading ? t('common.loading') : t('common.next')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
